import { useEffect, useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface CodeFormats {
  vendor: string;
  account: string;
  default: string;
  tokenDoc: string;
}

const SAMPLE_NAME = "Acme Corporation";

function previewToken(template: string, name: string): string {
  const cleaned = name.toUpperCase().replace(/[^A-Z0-9]/g, "");
  return template
    .replace(/\{NAME:(\d+)\}/g, (_m, n) => cleaned.slice(0, parseInt(n, 10)).padEnd(parseInt(n, 10), "X"))
    .replace(/\{SEQ:(\d+)\}/g, (_m, n) => "1".padStart(parseInt(n, 10), "0"))
    .replace(/\{YEAR\}/g, String(new Date().getFullYear()))
    .toUpperCase();
}

export default function CodeFormatsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [vendorTpl, setVendorTpl] = useState("");
  const [accountTpl, setAccountTpl] = useState("");

  const { data, isLoading } = useQuery<CodeFormats>({
    queryKey: ["/api/code-formats"],
    queryFn: () => api.get<CodeFormats>(`/code-formats`, sessionId),
  });

  useEffect(() => {
    if (data) {
      setVendorTpl(data.vendor);
      setAccountTpl(data.account);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.patch(`/code-formats`, { vendor: vendorTpl, account: accountTpl }, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/code-formats"] });
      toast({ title: "Code formats saved" });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Code Formats"
        description="Templates control auto-generated codes for new vendors and accounts."
      />

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Templates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            The default is{" "}
            <code className="px-1 rounded bg-muted">{data?.default}</code>.
          </p>
          <pre className="text-xs bg-muted/40 border rounded p-3 whitespace-pre-wrap" data-testid="text-token-doc">
            {data?.tokenDoc}
          </pre>

          <div className="space-y-2">
            <Label htmlFor="vendor-tpl">Vendor template</Label>
            <Input
              id="vendor-tpl"
              value={vendorTpl}
              onChange={(e) => setVendorTpl(e.target.value)}
              placeholder="{NAME:3}"
              data-testid="input-vendor-template"
            />
            <p className="text-xs text-muted-foreground">
              Preview for "{SAMPLE_NAME}":{" "}
              <code className="px-1 rounded bg-muted" data-testid="text-vendor-preview">{previewToken(vendorTpl || "", SAMPLE_NAME)}</code>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-tpl">Account template</Label>
            <Input
              id="account-tpl"
              value={accountTpl}
              onChange={(e) => setAccountTpl(e.target.value)}
              placeholder="{NAME:3}"
              data-testid="input-account-template"
            />
            <p className="text-xs text-muted-foreground">
              Preview for "{SAMPLE_NAME}":{" "}
              <code className="px-1 rounded bg-muted" data-testid="text-account-preview">{previewToken(accountTpl || "", SAMPLE_NAME)}</code>
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-code-formats"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
